import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import {
    BatchObject,
    BatchService,
    DialogConfig,
    DialogStyle,
    DisplayMode,
    EmptyStructuredQuery,
    Paginated,
    PaginatedBatch,
    ShDynamicDialogService,
    startDateDescSortInfo
} from '@stylehaven/components';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';

// from self "digihaven shared" part - must not use "@digihaven/shared" alias import, so to avoid circular dependencies (will cause trouble on test files, not production code)
import { DavBatchTaskType } from '../../entities/batches/dav-batch-task-type.entity';
import { DigihavenEnvironmentConfig } from '../../entities/config/environment-config.entity';
import { Organisation } from '../../entities/organisation/organisation.entity';
import { PaginatedResults } from '../../record/entities/pagination/paginated-results.entity';
import { PaginationInfo } from '../../record/entities/pagination/pagination-info.entity';
import { ConfigService } from '../../services/config/config.service';
import { BatchErrorDialogComponent } from '../containers/batch-error-dialog/batch-error-dialog.component';

@Injectable()
export class BatchesSandboxService {
    organisationInfo$: Observable<Organisation>;

    constructor(
        private batchService: BatchService,
        private readonly translateService: TranslateService,
        private dialogService: ShDynamicDialogService,
        private readonly configService: ConfigService<DigihavenEnvironmentConfig>
    ) {}

    // #region Batches
    private readonly BATCH_TYPES: DavBatchTaskType[] = [
        DavBatchTaskType.BATCH_SUBMIT_DOSSIERS_TASK,
        DavBatchTaskType.BATCH_DEPOT_DESTRUCTION,
        DavBatchTaskType.BATCH_DEPOT_DESTRUCTION_CSV,
        DavBatchTaskType.BATCH_CSV_TASK
    ];

    public getBatchList(paginationInfo: PaginationInfo): Observable<PaginatedResults<PaginatedBatch>> {
        const searchState = {
            structuredQuery: EmptyStructuredQuery,
            page: paginationInfo.page,
            pageSize: paginationInfo.resultsPerPage,
            sortInfo: startDateDescSortInfo,
            view: DisplayMode.LIST,
            taskType: this.BATCH_TYPES
        };
        return this.batchService.getBatchList(searchState).pipe(
            map(paginatedResult => this.mapToPaginatedBatchResult(paginatedResult, paginationInfo)),
            shareReplay(1)
        );
    }

    public openErrorDialog(batch: PaginatedBatch): void {
        const title$: Observable<string> = batch.Status$.pipe(
            map(status => this.translateService.instant(`batches.error-dialog.title.${status}`))
        );

        combineLatest([title$, batch.Status$])
            .pipe(take(1))
            .subscribe(([title, status]) => {
                const dialogConfig: DialogConfig = {
                    title,
                    options: {
                        style: DialogStyle.ExtraWide
                    },
                    data: {
                        batch,
                        status,
                        adminEmail: this.configService.config.adminMail
                    }
                };
                this.dialogService.open(BatchErrorDialogComponent, dialogConfig);
            });
    }
    // #endregion

    // #region Utils
    private mapToPaginatedBatchResult(
        paginatedResult: Paginated<BatchObject>,
        paginationInfo: PaginationInfo
    ): PaginatedResults<PaginatedBatch> {
        return {
            results: paginatedResult.Results.map(batch => new PaginatedBatch(batch, of([]), of(null))),
            paginationInfo: {
                ...paginationInfo,
                totalNumberOfResults: paginatedResult.TotalNrOfResults
            }
        };
    }
    // #endregion
}
